import type { SxProps, Theme } from '@mui/material';

export type MessageGroupPosition = 'single' | 'first' | 'middle' | 'last';

type MessageLike = {
  is_from_me?: boolean;
  created_at?: string | null;
};

function messageDayKey(createdAt?: string | null): string | null {
  if (!createdAt) return null;
  const match = String(createdAt).match(/^(\d{4}-\d{2}-\d{2})/);
  if (match?.[1]) return match[1];
  const normalized = createdAt.includes('T') ? createdAt : createdAt.replace(' ', 'T');
  const d = new Date(normalized);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function getMessageGroupPosition<T extends MessageLike>(
  messages: T[],
  index: number
): MessageGroupPosition {
  const current = messages[index];
  const prev = messages[index - 1];
  const next = messages[index + 1];
  const currentDay = messageDayKey(current.created_at);
  const sameAsPrev =
    !!prev &&
    !!prev.is_from_me === !!current.is_from_me &&
    messageDayKey(prev.created_at) === currentDay;
  const sameAsNext =
    !!next &&
    !!next.is_from_me === !!current.is_from_me &&
    messageDayKey(next.created_at) === currentDay;

  if (!sameAsPrev && !sameAsNext) return 'single';
  if (!sameAsPrev && sameAsNext) return 'first';
  if (sameAsPrev && sameAsNext) return 'middle';
  return 'last';
}

/**
 * Forme iMessage : coins arrondis + pointe en bas dessinée avec 2 pseudo-éléments
 * (la pointe reprend la couleur de la bulle, puis un masque de la couleur de fond la galbe).
 *
 * @param maskColor Couleur de fond du fil de discussion (pour masquer la pointe).
 */
export function getAppleMessageBubbleSx(
  outgoing: boolean,
  backgroundColor: string,
  groupPosition: MessageGroupPosition,
  maskColor: string
): SxProps<Theme> {
  const radius = '18px';
  const showTail = groupPosition === 'single' || groupPosition === 'last';

  const tailSx: SxProps<Theme> = showTail
    ? outgoing
      ? {
          '&::before': {
            content: '""',
            position: 'absolute',
            zIndex: 0,
            bottom: 0,
            right: -8,
            width: 20,
            height: 20,
            bgcolor: backgroundColor,
            borderBottomLeftRadius: '16px',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            zIndex: 1,
            bottom: 0,
            right: -20,
            width: 20,
            height: 20,
            bgcolor: maskColor,
            borderBottomLeftRadius: '12px',
          },
        }
      : {
          '&::before': {
            content: '""',
            position: 'absolute',
            zIndex: 0,
            bottom: 0,
            left: -8,
            width: 20,
            height: 20,
            bgcolor: backgroundColor,
            borderBottomRightRadius: '16px',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            zIndex: 1,
            bottom: 0,
            left: -20,
            width: 20,
            height: 20,
            bgcolor: maskColor,
            borderBottomRightRadius: '12px',
          },
        }
    : {};

  return {
    position: 'relative',
    borderRadius: radius,
    bgcolor: backgroundColor,
    ...tailSx,
  };
}

export function getMessageGroupSpacing(groupPosition: MessageGroupPosition): number {
  return groupPosition === 'last' || groupPosition === 'single' ? 1.75 : 0.3;
}
